import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import { createApp } from 'vue'
import { createGtm } from '@gtm-support/vue-gtm'
import App from './App.vue'
import './css/index.css'
import router from './router'
import store from './store'
import { createPinia } from 'pinia'
import focus from '@/directives/focus/focusDirective'
import editable from '@/directives/editableDirective'
import editableSwitch from '@/directives/editableSwitchDirective'
import autoResizeDirective from './directives/autoResizeDirective'
import wordCountDirective from './directives/wordCountDirective'
import userActions from '@/store/userModule/actionTypes'
import { createI18n } from 'vue-i18n'
import strings from '@/lang'
import snippet from '@segment/snippet'
import FontAwesomeIcon from '@assets/fontawesome-icons'
import { FeatureFlags } from './types'
import { RudderAnalytics } from '@rudderstack/analytics-js'
import posthog from 'posthog-js'
import renderTooltip from './plugins/renderTooltip'
import { failedPaymentWall, loadChurnkey } from './lib/churnkey'
import { env } from '@/lib/runtimeEnv'

// Initialize RudderStack
if (env.VITE_RUDDERSTACK_WRITE_KEY && env.VITE_RUDDERSTACK_DATAPLANE_URL) {
  const rsAnalytics = new RudderAnalytics()
  rsAnalytics.load(
    env.VITE_RUDDERSTACK_WRITE_KEY as string,
    env.VITE_RUDDERSTACK_DATAPLANE_URL as string
  )
  window.rudderanalytics = rsAnalytics
}

// Initialize PostHog
if (env.VITE_POSTHOG_API_KEY && env.VITE_POSTHOG_HOST) {
  posthog.init(env.VITE_POSTHOG_API_KEY as string, {
    api_host: env.VITE_POSTHOG_HOST as string,
    person_profiles: 'always', // or 'always' to create profiles for anonymous users as well
    capture_pageview: 'history_change',
    disable_web_experiments: true,
    autocapture: {
      url_ignorelist: ['app.jobscan.co/auth/*', 'app.jobscan.co/auth/sso/*', 'app.jobscan.co/resume-manager', 'app.jobscan.co/forgot-password', 'app.jobscan.co/reset-password', 'app.jobscan.co/account-settings', 'app.jobscan.co/reset-password', 'app.jobscan.co/coach', 'app.jobscan.co/verify-email', 'app.jobscan.co/match-report-print', 'app.jobscan.co/scan/1', 'app.jobscan.co/password-changed', 'app.jobscan.co/password-reset-sent', 'app.jobscan.co/resume-builder/*']
    }
  })
  window.posthog = posthog
}

// load segment snippet
if (env.VITE_SEGMENT_KEY) {
  const segmentSnippet = snippet.max({
    host: 'cdn.segment.com',
    apiKey: env.VITE_SEGMENT_KEY as string,
  })
  //insert script into document head
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.innerHTML = segmentSnippet
  document.head.appendChild(script)
}

const app = createApp(App)
app.use(renderTooltip)

// Set up feature flags
const featureFlags = {
  powerEdit3: env.VITE_ENABLE_POWER_EDIT_3 === 'true',
  powerEditInlineEdit: true,
  impersonation: env.VITE_ENABLE_IMPERSONATION === 'true',
  onboarding: env.VITE_ENABLE_ONBOARDING === 'true',
  linkedinToBase: env.VITE_ENABLE_LINKEDIN_TO_BASE === 'true',
  failedPaymentWall: env.VITE_ENABLE_FAILED_PAYMENT_WALL === 'true',
  churnkeyCancelFlow: env.VITE_ENABLE_CHURNKEY_CANCEL_FLOW === 'true',
  lio2: env.VITE_ENABLE_LIO_2 === 'true',
  matchReport: env.VITE_ENABLE_MATCH_REPORT === 'true',
  networkingMessageGenerator:
    env.VITE_ENABLE_NETWORKING_MESSAGE_GENERATOR === 'true',
  resumeBuilderRecommendedJobs:
    env.VITE_ENABLE_RESUME_BUILDER_RECOMMENDED_JOBS === 'true',
  opportunityCustomSkills:
    env.VITE_ENABLE_OPPORTUNITY_CUSTOM_SKILLS === 'true',
  facebookLogin: env.VITE_ENABLE_FACEBOOK_LOGIN === 'true',
  resumeScan: env.VITE_ENABLE_RESUME_SCAN === 'true',
  resumeScanEditMode: env.VITE_ENABLE_RESUME_SCAN_EDIT_MODE === 'true',
  resumeFormattingOptions: env.VITE_ENABLE_RESUME_FORMATTING_OPTIONS === 'true',
}
app.provide<FeatureFlags>('featureFlags', featureFlags)

// Set up Fontawesome
app.component('FontAwesomeIcon', FontAwesomeIcon)

// Set up Google Tag Manager
if (env.VITE_GTM_ID) {
  const gtm = createGtm({ id: env.VITE_GTM_ID as string })
  app.use(gtm)
}

// Set up Bugsnag
if (env.VITE_BUGSNAG_KEY) {
  Bugsnag.start({
    apiKey: env.VITE_BUGSNAG_KEY as string,
    plugins: [new BugsnagPluginVue()],
    releaseStage: env.VITE_RELEASE_STAGE as string,
    enabledReleaseStages: ['production', 'staging'],
    appVersion: (env.VITE_TAG_NAME as string) || 'unknown',
  })
  const bugsnagVue = Bugsnag.getPlugin('vue')
  bugsnagVue && app.use(bugsnagVue)
}

// Setup Churnkey
if (featureFlags.failedPaymentWall || featureFlags.churnkeyCancelFlow) {
  const churnkeyTag = loadChurnkey(env.VITE_CHURNKEY_APP_ID as string)
  if (churnkeyTag) {
    document.head.appendChild(churnkeyTag)
  }
}

const i18n = createI18n({
  legacy: false,
  locale: 'en',
  messages: strings,
})

app.use(i18n)
app.use(store)

app.directive('autoResize', autoResizeDirective)
app.directive('wordCount', wordCountDirective)
app.directive('focus', focus)
app.directive('editableSwitch', editableSwitch) // This flags the direct editing of resume content in PowerEdit 3
if (featureFlags.powerEditInlineEdit) {
  app.directive('editable', editable) // This flags the direct editing of resume content in PowerEdit 3
}

// async preload before mounting
; (async () => {
  return store
    .dispatch(userActions.FETCH_USER.NAMESPACED)
    .then(user => {
      if (!user) {
        return
      }

      if (env.VITE_BUGSNAG_KEY) {
        Bugsnag.addOnError(function (evt) {
          evt.setUser(user.id)
        })

        Bugsnag.addMetadata('userInfo', {
          plan: user.plan,
          domain: user.domain?.domain,
          createdAt: user.createdAt,
        })
      }

      if (featureFlags.failedPaymentWall) {
        failedPaymentWall(user, {
          appId: env.VITE_CHURNKEY_APP_ID as string,
          mode: env.VITE_CHURNKEY_ENV as string,
          subscriptionId: user.subscription?.stripeId,
        })
      }
    })
    .catch(err => {
      console.debug(`No active session. Mounting application.`, err)
    })
    .finally(() => {
      app.use(router)
      // Set up Pinia
      app.use(createPinia())
      app.mount('#app')
    })
})()
